import { useEffect, useState } from "react";

import {
  CUSTOMER_TAB_NAME,
  WORK_FOR_CHANGED,
  readStoredWorkFor,
} from "@penn/web-common";

import { runtimeEnv } from "@/config/runtime-env";

/**
 * Opens the CUSTOMER host in a new tab, already acting for the selected
 * customer. A plain anchor, not a JS gesture, so popup blockers never apply.
 *
 * Selection and navigation stay separate controls: the dropdown also sets
 * context for this host's own screens, and changing the target has no effect on
 * My Tasks, so picking someone is not by itself a request to go and be them.
 */
export function customerHandoffUrl(
  base: string | undefined,
  workForUserId: string | null,
): string | null {
  if (!base || !workForUserId) return null;
  try {
    // Operator-supplied, but parsed rather than concatenated: a malformed value
    // must not become a `javascript:` href.
    const url = new URL("/work-for/open", base);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    url.searchParams.set("workForUserId", workForUserId);
    return url.toString();
  } catch {
    return null;
  }
}

type StoredTarget = { userId: string; email: string } | null;

function storedRegisteredTarget(): StoredTarget {
  const stored = readStoredWorkFor();
  return stored.kind === "registered"
    ? { userId: stored.userId, email: stored.email }
    : null;
}

type OpenAsCustomerLinkProps = {
  /** `band` is the slate-and-amber pill. `button` is for a white surface,
   *  where amber reads as mustard. */
  variant?: "band" | "button";
};

export function OpenAsCustomerLink({
  variant = "band",
}: OpenAsCustomerLinkProps) {
  const [target, setTarget] = useState<StoredTarget>(storedRegisteredTarget);

  // The target lives in sessionStorage, which React cannot observe; the
  // selector broadcasts this event on a real change.
  useEffect(() => {
    const sync = () => setTarget(storedRegisteredTarget());
    window.addEventListener(WORK_FOR_CHANGED, sync);
    return () => window.removeEventListener(WORK_FOR_CHANGED, sync);
  }, []);

  const href = customerHandoffUrl(
    runtimeEnv("VITE_WEB_CUSTOMER_URL"),
    target?.userId ?? null,
  );
  if (!href) return null;

  const isButton = variant === "button";

  return (
    <a
      href={href}
      // Our own customer site: no noopener, or the browser could not find and
      // reuse the named customer tab on the next click.
      target={CUSTOMER_TAB_NAME}
      title="Open the customer site, acting for this customer"
      className={
        isButton
          ? "inline-flex items-center rounded-xl bg-penn-700 px-4 py-2 text-sm font-semibold text-white hover:bg-penn-800"
          : "shrink-0 rounded border border-amber-300/60 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-amber-300 hover:bg-amber-300/10 whitespace-nowrap"
      }
    >
      {isButton && target ? `Work as ${target.email}` : "OPEN AS CUSTOMER"}
    </a>
  );
}
